"use client";

import {
  CircleCheck,
  Database,
  File,
  AlertCircle,
  ChevronLeft,
  RefreshCw,
  Trash2,
  Upload,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";

import React, { useState, useRef, useCallback, useEffect } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Progress } from "@/components/ui/progress";
import { useQueryClient } from "@tanstack/react-query";
import { fetchData } from "@/utils/api-handler";
import { useProtectedQuery } from "@/hooks/use-protected-query";
import BackgroundUploadService, {
  UploadTask,
  UploadError,
} from "@/lib/background-uploader";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import {
  formatDocumentFileSize,
  getDefaultDocumentGroupId,
  getDocumentFileAcceptValue,
  getDocumentFileTypeLabel,
  getDocumentFileValidationError,
  MAX_DOCUMENT_UPLOAD_FILES,
  SUPPORTED_DOCUMENT_FORMAT_LABELS,
} from "@/utils/document-upload";

interface DocumentUploadProps {
  activeSection?: string;
  setActiveSection?: React.Dispatch<React.SetStateAction<string>>;
}

interface DataGroup {
  id: string;
  name: string;
  description: string;
  is_default?: boolean;
}

interface DataSource {
  id: string;
  name: string;
  description: string;
  created_at: string;
  groups: DataGroup[];
}

interface DataSourceWithGroupsResponse {
  datasources: DataSource[];
}

interface SelectedFile {
  file: File;
  id: string;
  preview?: string;
  error?: string;
}

function FormatSupportChips() {
  return (
    <div className="flex flex-wrap gap-1.5">
      {SUPPORTED_DOCUMENT_FORMAT_LABELS.map((format) => (
        <Badge
          key={format}
          variant="outline"
          className="rounded-md border-border/60 bg-background px-2 py-0.5 text-[11px] font-medium text-muted-foreground"
        >
          {format}
        </Badge>
      ))}
    </div>
  );
}

export const DocumentUpload: React.FC<DocumentUploadProps> = ({
  setActiveSection,
}) => {
  const [selectedFiles, setSelectedFiles] = useState<SelectedFile[]>([]);
  const [selectedDataSource, setSelectedDataSource] = useState<string>("");
  const [selectedGroup, setSelectedGroup] = useState<string>("");
  const [isDragOver, setIsDragOver] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadTasks, setUploadTasks] = useState<UploadTask[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragCounterRef = useRef(0);
  const queryClient = useQueryClient();
  const scheduledRemovals = useRef<Set<string>>(new Set());

  const { data: datasourcesData, isLoading: loadingDataSources, hasPermission: hasListDataSourcesPermission } =
    useProtectedQuery<DataSourceWithGroupsResponse>({
      queryKey: ["datasources-with-groups"],
      queryFn: async () => {
        return fetchData("/api/documents/datasources/all");
      },
      requiredPermissions: ["LIST_DATA_SOURCES"],
    });

  useEffect(() => {
    if (!selectedDataSource) {
      setSelectedGroup("");
      return;
    }

    const groups =
      datasourcesData?.datasources?.find((ds) => ds.id === selectedDataSource)
        ?.groups ?? [];

    const defaultGroupId = getDefaultDocumentGroupId(groups);
    setSelectedGroup(defaultGroupId ?? "");
  }, [selectedDataSource, datasourcesData]);

  // Subscribe to upload progress
  useEffect(() => {
    const uploadService = BackgroundUploadService.getInstance();
    const unsubscribe = uploadService.subscribe((tasks) => {
      setUploadTasks(tasks);

      // Auto-remove completed or failed tasks after 5 seconds
      tasks.forEach((task) => {
        if (
          (task.status === "completed" || task.status === "failed") &&
          !scheduledRemovals.current.has(task.id)
        ) {
          scheduledRemovals.current.add(task.id);
          setTimeout(() => {
            uploadService.removeTask(task.id);
            scheduledRemovals.current.delete(task.id);
          }, 5000);
        }
      });
    });

    // Clean up on unmount
    return () => {
      unsubscribe();
      // Clear any pending timeouts
      scheduledRemovals.current.clear();
    };
  }, []);

  const handleFileSelect = useCallback(
    (files: FileList | null) => {
      if (!files) return;

      const newFiles: SelectedFile[] = [];
      const existingFileNames = new Set(selectedFiles.map((f) => f.file.name));
      const currentFileCount = selectedFiles.length;

      Array.from(files).forEach((file) => {
        // Check if we've reached the maximum file limit
        if (currentFileCount + newFiles.length >= MAX_DOCUMENT_UPLOAD_FILES) {
          toast.warning(
            `Maximum ${MAX_DOCUMENT_UPLOAD_FILES} files allowed. Please remove some files first.`
          );
          return;
        }

        // Check if file already exists in selected files
        if (existingFileNames.has(file.name)) {
          toast.warning(`${file.name} is already selected`);
          return;
        }

        const error = getDocumentFileValidationError(file);
        const id = `${file.name}-${Date.now()}-${Math.random()}`;

        newFiles.push({
          file,
          id,
          error: error || undefined,
        });
      });

      if (newFiles.length > 0) {
        setSelectedFiles((prev) => [...prev, ...newFiles]);
      }
    },
    [selectedFiles]
  );

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current += 1;
    setIsDragOver(true);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current -= 1;
    if (dragCounterRef.current <= 0) {
      dragCounterRef.current = 0;
      setIsDragOver(false);
    }
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounterRef.current = 0;
      setIsDragOver(false);

      if (!selectedDataSource) {
        toast.error("Please select a data source first");
        return;
      }

      handleFileSelect(e.dataTransfer.files);
    },
    [handleFileSelect, selectedDataSource]
  );

  const openFilePicker = useCallback(() => {
    if (!selectedDataSource) {
      toast.error("Please select a data source first");
      return;
    }
    fileInputRef.current?.click();
  }, [selectedDataSource]);

  const removeFile = (id: string) => {
    setSelectedFiles((prev) => prev.filter((f) => f.id !== id));
  };

  const handleUploadError = (file: File, id: string, error: UploadError) => {
    // Use the error message from backend, fallback to a generic message
    const errorMessage = error?.message || "Upload failed";

    // Update file status with error
    setSelectedFiles((prev) =>
      prev.map((f) => (f.id === id ? { ...f, error: errorMessage } : f))
    );

    // Determine error type for appropriate handling
    const errorCode = error?.error_code || "";
    const isPermissionError = [
      "DOCUMENT__UNAUTHORIZED_UPLOAD_ACCESS",
      "DOCUMENT__ACCESS_DENIED",
    ].includes(errorCode);
    const isQuotaError = errorCode === "DOCUMENT__QUOTA_EXCEEDED";
    const isSessionError = [
      "DOCUMENT__UPLOAD_SESSION_NOT_FOUND",
      "DOCUMENT__UPLOAD_SESSION_EXPIRED",
    ].includes(errorCode);
    const isDuplicateError = errorCode === "DOCUMENT__ALREADY_EXISTS";

    // Show toast notification with dynamic message
    toast.error(`${file.name}: ${errorMessage}`, {
      description:
        error?.details ||
        (isPermissionError
          ? "Please contact your administrator for access"
          : isQuotaError
            ? "Please delete some files or contact support to increase your quota"
            : isSessionError
              ? "Your upload session has expired. Please try uploading again"
              : isDuplicateError
                ? "Please rename the file or delete the existing one"
                : "Please try again or contact support if the issue persists"),
      duration: isPermissionError || isQuotaError ? 10000 : 5000,
    });

    // If it's a critical error that affects the whole upload process
    if (isPermissionError || isQuotaError) {
      setIsUploading(false);
      // Cancel all pending uploads
      const uploadService = BackgroundUploadService.getInstance();
      uploadTasks
        .filter((task) => task.status === "pending")
        .forEach((task) => {
          uploadService.cancelUpload(task.id);
        });
    }

    // Automatically remove the error from the file after 5 seconds
    setTimeout(() => {
      setSelectedFiles((prev) => prev.filter((f) => f.id !== id));
    }, 5000);
  };

  const handleUpload = async () => {
    if (!selectedDataSource) {
      toast.error("Please select a data source");
      return;
    }

    if (!selectedGroup) {
      toast.error("No default group found for this data source");
      return;
    }

    const validFiles = selectedFiles.filter((f) => !f.error);
    if (validFiles.length === 0) {
      toast.error("No valid files to upload");
      return;
    }

    setIsUploading(true);

    try {
      const uploadService = BackgroundUploadService.getInstance();

      // Check for files already in upload queue
      const currentTasks = uploadService.getAllTasks();
      const uploadingFileNames = new Set(
        currentTasks
          .filter(
            (task) => task.status === "pending" || task.status === "uploading"
          )
          .map((task) => task.file.name)
      );

      const filesToUpload = validFiles.filter(({ file }) => {
        if (uploadingFileNames.has(file.name)) {
          toast.warning(`${file.name} is already in the upload queue`);
          return false;
        }
        return true;
      });

      if (filesToUpload.length === 0) {
        toast.warning("All selected files are already being uploaded");
        setIsUploading(false);
        return;
      }

      filesToUpload.forEach(({ file, id }) => {
        uploadService.addUpload(
          file,
          selectedDataSource,
          selectedGroup,
          `Uploaded ${file.name}`,
          (error) => {
            if (error) {
              handleUploadError(file, id, error);
            }
          }
        );
      });

      toast.success(`${filesToUpload.length} file(s) added to upload queue`);

      // Clear successfully queued files immediately
      setSelectedFiles([]);
    } catch (error) {
      toast.error("Failed to start upload process", {
        description:
          "There was an error initializing the upload. Please try again.",
      });
      console.error("Upload error:", error);
    } finally {
      setIsUploading(false);
      queryClient.invalidateQueries({
        queryKey: ["documents-files"],
      });
    }
  };

  const validFiles = selectedFiles.filter((f) => !f.error);
  const invalidFiles = selectedFiles.filter((f) => f.error);
  const canUpload =
    validFiles.length > 0 &&
    selectedDataSource &&
    selectedGroup &&
    !isUploading;

  // Calculate overall progress
  const activeUploads = uploadTasks.filter(
    (task) => task.status === "uploading" || task.status === "pending"
  );
  const completedUploads = uploadTasks.filter(
    (task) => task.status === "completed"
  );

  const selectedDataSourceName = datasourcesData?.datasources?.find(
    (ds) => ds.id === selectedDataSource
  )?.name;

  return (
    <div className="flex w-full flex-col">
      <div className="mb-5 flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setActiveSection?.("list")}
          className="shrink-0"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <div className="flex min-w-0 items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[9px] bg-violet-100 dark:bg-violet-900/30">
            <Upload className="h-5 w-5 text-primary" />
          </div>
          <div className="min-w-0">
            <h2 className="text-lg font-bold sm:text-2xl">Upload Documents</h2>
            <p className="hidden text-sm text-muted-foreground sm:block">
              Add files to your data source
            </p>
          </div>
        </div>
      </div>

      <Card className="w-full overflow-hidden border shadow-sm">
        <CardContent className="space-y-5 p-4 sm:p-6">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Database className="h-4 w-4 text-primary" />
              <Label htmlFor="datasource" className="text-sm font-medium">
                Data source
              </Label>
              <span className="text-sm text-destructive">*</span>
            </div>
            <Select
              value={selectedDataSource}
              onValueChange={setSelectedDataSource}
              disabled={
                loadingDataSources ||
                (!loadingDataSources &&
                  (!datasourcesData?.datasources ||
                    datasourcesData.datasources.length === 0))
              }
            >
              <SelectTrigger id="datasource" className="h-11 w-full bg-background">
                <SelectValue
                  placeholder={
                    !loadingDataSources &&
                    (!datasourcesData?.datasources ||
                      datasourcesData.datasources.length === 0)
                      ? "No data sources available"
                      : "Select a data source"
                  }
                >
                  {selectedDataSourceName ? (
                    <span className="truncate font-medium">{selectedDataSourceName}</span>
                  ) : null}
                </SelectValue>
              </SelectTrigger>
              <SelectContent className="max-h-[240px]">
                {datasourcesData?.datasources?.map((datasource) => (
                  <SelectItem
                    key={datasource.id}
                    value={datasource.id}
                    textValue={datasource.name}
                  >
                    <div className="flex flex-col items-start gap-0.5 py-0.5">
                      <span className="font-medium leading-none">{datasource.name}</span>
                      <span className="text-xs text-muted-foreground">
                        Created{" "}
                        {new Date(datasource.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {loadingDataSources && (
              <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <RefreshCw className="h-3 w-3 animate-spin" />
                Loading data sources...
              </p>
            )}
            {!loadingDataSources &&
              (!datasourcesData?.datasources ||
                datasourcesData.datasources.length === 0) && (
                <p className="text-xs text-muted-foreground">
                  Create a data source first to upload documents.
                </p>
              )}
          </div>

          <div
            role="button"
            tabIndex={0}
            className={cn(
              "flex w-full cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed px-4 py-12 text-center transition-all sm:py-16",
              isDragOver
                ? "border-primary bg-primary/5"
                : "border-muted-foreground/25 bg-muted/10 hover:border-primary/40 hover:bg-muted/30"
            )}
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={openFilePicker}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                openFilePicker();
              }
            }}
          >
            <span className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-violet-100 dark:bg-violet-900/30">
              <Upload className="h-7 w-7 text-primary" />
            </span>
            <p className="text-lg font-semibold text-foreground">
              Drop files here or click to browse
            </p>
            <p className="mt-2 text-sm text-muted-foreground">
              Up to {MAX_DOCUMENT_UPLOAD_FILES} files · 100MB per file
            </p>
            {selectedDataSourceName && (
              <p className="mt-1 text-xs text-muted-foreground">
                Uploading to{" "}
                <span className="font-medium text-foreground">
                  {selectedDataSourceName}
                </span>
              </p>
            )}
          </div>

          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={getDocumentFileAcceptValue()}
            onChange={(e) => handleFileSelect(e.target.files)}
            className="hidden"
          />

          <div className="rounded-lg border border-dashed bg-muted/20 px-4 py-3">
            <p className="mb-2 text-xs font-medium text-muted-foreground">
              Supported formats
            </p>
            <FormatSupportChips />
          </div>

              {selectedFiles.length > 0 && (
                <div className="space-y-3 rounded-lg border bg-background p-4">
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-sm font-medium">
                      Selected files ({selectedFiles.length})
                    </p>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-8 text-muted-foreground"
                      onClick={() => setSelectedFiles([])}
                    >
                      Clear all
                    </Button>
                  </div>
                  <div className="max-h-56 space-y-2 overflow-y-auto">
                    {selectedFiles.map((selectedFile) => (
                      <div
                        key={selectedFile.id}
                        className={cn(
                          "flex items-center justify-between gap-3 rounded-lg border px-3 py-2.5",
                          selectedFile.error
                            ? "border-destructive/30 bg-destructive/5"
                            : "border-border bg-muted/20"
                        )}
                      >
                        <div className="flex min-w-0 flex-1 items-center gap-3">
                          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-background border">
                            <File className="h-4 w-4 text-muted-foreground" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-medium">
                              {selectedFile.file.name}
                            </p>
                            <div className="mt-0.5 flex flex-wrap items-center gap-2">
                              <span className="text-xs text-muted-foreground">
                                {formatDocumentFileSize(selectedFile.file.size)}
                              </span>
                              <Badge variant="secondary" className="text-[10px]">
                                {getDocumentFileTypeLabel(selectedFile.file)}
                              </Badge>
                            </div>
                            {selectedFile.error && (
                              <p className="mt-1 flex items-center gap-1 text-xs text-destructive">
                                <AlertCircle className="h-3 w-3 shrink-0" />
                                {selectedFile.error}
                              </p>
                            )}
                          </div>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
                          onClick={() => removeFile(selectedFile.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                  {invalidFiles.length > 0 && (
                    <Alert variant="destructive" className="py-2">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription className="text-xs">
                        {invalidFiles.length} file(s) have errors and will not be
                        uploaded.
                      </AlertDescription>
                    </Alert>
                  )}
                </div>
              )}

              {uploadTasks.length > 0 && (
                <div className="space-y-3 rounded-lg border bg-background p-4">
                  <div className="flex items-center justify-between gap-3 border-b pb-3">
                    <div>
                      <p className="text-sm font-medium">Upload progress</p>
                      <p className="text-xs text-muted-foreground">
                        {activeUploads.length} in progress
                      </p>
                    </div>
                    <Badge variant="secondary">
                      {completedUploads.length}/{uploadTasks.length} done
                    </Badge>
                  </div>
                  <div className="max-h-56 space-y-2 overflow-y-auto">
                    {uploadTasks.slice(0, 5).map((task) => (
                      <div
                        key={task.id}
                        className="rounded-lg border bg-muted/20 p-3"
                      >
                        <div className="mb-2 flex items-start justify-between gap-3">
                          <div className="flex min-w-0 flex-1 items-center gap-2">
                            {task.status === "completed" && (
                              <CircleCheck className="h-4 w-4 shrink-0 text-green-600 dark:text-green-500" />
                            )}
                            {task.status === "uploading" && (
                              <RefreshCw className="h-4 w-4 shrink-0 animate-spin text-primary" />
                            )}
                            {task.status === "failed" && (
                              <AlertCircle className="h-4 w-4 shrink-0 text-destructive" />
                            )}
                            {task.status === "pending" && (
                              <div className="h-4 w-4 shrink-0 rounded-full border-2 border-muted-foreground/40" />
                            )}
                            <div className="min-w-0">
                              <p className="truncate text-sm font-medium">
                                {task.file.name}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {formatDocumentFileSize(task.file.size)}
                              </p>
                            </div>
                          </div>
                          <span
                            className={cn(
                              "text-sm font-semibold",
                              task.status === "completed" && "text-green-600 dark:text-green-500",
                              task.status === "failed" && "text-destructive",
                              (task.status === "uploading" || task.status === "pending") &&
                                "text-primary"
                            )}
                          >
                            {Math.round(task.progress)}%
                          </span>
                        </div>
                        <Progress value={task.progress} className="h-1.5" />
                        {task.status === "failed" && task.error && (
                          <p className="mt-2 text-xs text-destructive">{task.error}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>

        <div className="flex justify-end gap-3 border-t bg-muted/20 px-4 py-4 sm:px-6">
          <Button variant="outline" onClick={() => setActiveSection?.("list")}>
            Cancel
          </Button>
          <Button onClick={handleUpload} disabled={!canUpload} className="min-w-[140px]">
            {isUploading ? (
              <>
                <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                Starting...
              </>
            ) : (
              <>
                <Upload className="mr-2 h-4 w-4" />
                Upload {validFiles.length > 0 ? validFiles.length : ""} file
                {validFiles.length === 1 ? "" : "s"}
              </>
            )}
          </Button>
        </div>
      </Card>
    </div>
  );
};

